/**
 * PasswordStrengthHint
 * Observes Supabase Auth UI's password input during sign-up
 * and shows a strength bar with advisory criteria.
 */

import { useState, useEffect, useCallback } from 'react';
import { validatePassword } from '@/utils/passwordValidation';
import type { PasswordValidationResult } from '@/utils/passwordValidation';
import type { PasswordStrengthHintProps } from './PasswordStrengthHint.types';
import styles from './PasswordStrengthHint.module.css';

const INITIAL_RESULT: PasswordValidationResult = {
  strength: 'weak',
  score: 0,
  messages: [],
  isAcceptable: false,
};

const BAR_WIDTH: Record<string, string> = {
  weak: '33%',
  fair: '66%',
  strong: '100%',
};

function isSignUpPasswordInput(input: HTMLInputElement): boolean {
  return input.id === 'password' && input.autocomplete === 'new-password';
}

function findSignUpPasswordInput(container: HTMLElement): HTMLInputElement | null {
  const inputs = container.querySelectorAll<HTMLInputElement>('input#password');
  for (const input of inputs) {
    if (isSignUpPasswordInput(input)) return input;
  }
  return null;
}

export function PasswordStrengthHint({ containerRef }: PasswordStrengthHintProps) {
  const [result, setResult] = useState<PasswordValidationResult>(INITIAL_RESULT);
  const [visible, setVisible] = useState(false);

  const handleInput = useCallback((e: Event) => {
    const input = e.target as HTMLInputElement;
    const value = input.value;
    if (value.length === 0) {
      setVisible(false);
      setResult(INITIAL_RESULT);
    } else {
      setVisible(true);
      setResult(validatePassword(value));
    }
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let currentInput: HTMLInputElement | null = null;

    function attach() {
      if (!container) return;
      const input = findSignUpPasswordInput(container);

      if (input && input !== currentInput) {
        currentInput?.removeEventListener('input', handleInput);
        currentInput = input;
        currentInput.addEventListener('input', handleInput);
        // Sync with any existing value
        if (input.value.length > 0) {
          setVisible(true);
          setResult(validatePassword(input.value));
        } else {
          setVisible(false);
          setResult(INITIAL_RESULT);
        }
      } else if (!input && currentInput) {
        currentInput.removeEventListener('input', handleInput);
        currentInput = null;
        setVisible(false);
        setResult(INITIAL_RESULT);
      }
    }

    // Initial scan
    attach();

    // Watch for DOM changes (view switches between sign-in/sign-up).
    // Observe both childList and attribute changes because Supabase Auth UI
    // may reuse the same input element and only toggle its autocomplete attribute.
    const observer = new MutationObserver(attach);
    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['autocomplete'],
    });

    return () => {
      observer.disconnect();
      currentInput?.removeEventListener('input', handleInput);
    };
  }, [containerRef, handleInput]);

  if (!visible) return null;

  return (
    <div className={styles.wrapper} role="status" aria-live="polite">
      <div className={styles.barTrack}>
        <div
          className={styles.barFill}
          data-strength={result.strength}
          style={{ width: BAR_WIDTH[result.strength] }}
        />
      </div>
      <div className={styles.label}>
        <span className={styles.strengthText} data-strength={result.strength}>
          {result.strength}
        </span>
        {result.messages.length > 0 && (
          <span className={styles.criteria}>{result.messages.length} remaining</span>
        )}
      </div>
      {result.messages.length > 0 && (
        <ul className={styles.criteriaList}>
          {result.messages.map((msg) => (
            <li key={msg}>{msg}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
